import { useState } from "react";
import { Tooltip } from "antd";
import { Copy, Check } from "lucide-react";
import { safeClipboardWrite } from "../utils/preview-utils";

interface PreviewMetaItemProps {
    icon: React.ReactNode;
    text: string;
    mono?: boolean;
    copyable?: string;
    /** "compact" for dense layouts, "default" matches PreviewPRList style */
    variant?: "compact" | "default";
}

export default function PreviewMetaItem({ icon, text, mono, copyable, variant = "compact" }: PreviewMetaItemProps) {
    const [copied, setCopied] = useState(false);

    const isDefault = variant === "default";
    const fontSize = isDefault ? 13 : 12;
    const color = isDefault ? "#4b5563" : "#6b7280";
    const gap = isDefault ? 7 : 5;
    const lineHeight = isDefault ? "18px" : "16px";
    const fontWeight = isDefault ? (mono ? 600 : 500) : (mono ? 500 : 400);
    const maxWidth = isDefault ? 200 : 180;
    const copyIconSize = isDefault ? 11 : 10;
    const copyOpacity = isDefault ? "0.3" : "0.4";

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!copyable) return;
        safeClipboardWrite(copyable);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const content = (
        <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap,
            fontSize,
            color,
            lineHeight,
        }}>
            <span style={{ color: "#9ca3af", display: "flex", alignItems: "center" }}>
                {icon}
            </span>
            <span style={{
                fontFamily: mono ? "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace" : "inherit",
                fontWeight,
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}>
                {text}
            </span>
            {copyable && (
                copied ? (
                    <Check
                        size={copyIconSize}
                        style={{ color: "#16a34a", flexShrink: 0, transition: "all 0.15s" }}
                    />
                ) : (
                    <Copy
                        size={copyIconSize}
                        aria-label="Copy to clipboard"
                        style={{ cursor: "pointer", opacity: parseFloat(copyOpacity), transition: "all 0.15s", flexShrink: 0 }}
                        onClick={handleCopy}
                        onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = "1"; }}
                        onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = copyOpacity; }}
                    />
                )
            )}
        </span>
    );

    if (copyable) {
        return <Tooltip title={copied ? "Copied!" : copyable}>{content}</Tooltip>;
    }
    return content;
}
